import styled, { keyframes } from 'styled-components'

// Animation
export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Container for the entire section
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
  container-type: inline-size;
  container-name: geo-analysis;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

// Header section components removed - now using AnalysisSectionHeader from @popety-io/ui

// Grid layout for cards
export const Grid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns || 3}, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  overflow: visible;
  position: relative;
  z-index: 1;

  /* Container queries - respond to container width, not viewport */
  @container geo-analysis (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @container geo-analysis (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  /* Fallback for browsers without container query support */
  @supports not (container-type: inline-size) {
    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`

// Card component
export const Card = styled.div<{ $highlight?: boolean; $span?: number }>`
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  grid-column: span ${(props) => props.$span || 1};
  position: relative;
  overflow: visible;

  ${(props) =>
    props.$highlight &&
    `
    background: linear-gradient(135deg, #00ADE6 0%, #00B341 100%);
    color: white;
    border: none;

    & ${CardTitle}, & ${MetricLabel}, & ${MetricValue} {
      color: white;
    }
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

export const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

// Score display component (keeping as requested)
export const ScoreCard = styled(Card)`
  background: linear-gradient(135deg, #00b341 0%, #00ade6 100%);
  color: white;
  text-align: center;
`

export const ScoreValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
`

export const ScoreLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 1px;
`

// Metric display components
export const MetricGrid = styled.div`
  display: grid;
  gap: 16px;
  overflow: visible;
  position: relative;
`

export const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  overflow: visible;
  position: relative;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`

export const MetricLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;
`

export const MetricValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const MetricHighlight = styled.span<{
  $type: 'success' | 'warning' | 'danger' | 'info'
  $textAlign?: 'left' | 'center' | 'right'
}>`
  font-size: 14px;
  font-weight: 600;
  text-align: ${(props) => (props.$textAlign ? props.$textAlign : 'left')};
  color: ${(props) =>
    props.$type === 'success'
      ? '#00B341'
      : props.$type === 'warning'
        ? '#FF9500'
        : props.$type === 'danger'
          ? '#FF3B30'
          : '#007AFF'};
`

// Icon container
export const IconContainer = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => props.$color || 'rgba(0, 173, 230, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

// Progress indicator
export const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin: 12px 0;
`

export const ProgressFill = styled.div<{
  $percentage: number
  $color?: string
}>`
  height: 100%;
  width: ${(props) => props.$percentage}%;
  background: ${(props) => props.$color || '#00ADE6'};
  border-radius: 3px;
  transition: width 0.3s ease;
`

// Tabs for different views
export const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  padding: 4px;
  border-radius: 8px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: none;
  background: ${(props) =>
    props.$active ? props.theme.colors.background.primary : 'transparent'};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.text.primary
      : props.theme.colors.text.secondary};
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? '600' : '500')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
  }
`

// Chart container
export const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
`

// Visualization container - for 3D charts
export const Visualization = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
`

// Boundary line style
export const Boundary = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.large} 0;
`
